import React, { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  PHASE1_COLUMN_HEADINGS,
  PHASE1_PAGES,
  phase1Path,
  type Phase1Module,
} from '@/lib/phase1Paths';

interface Phase1ColumnProps {
  module: Phase1Module;
  /** Render for the phone's More sheet — always open, taller rows for touch. */
  inDrawer?: boolean;
  onNavigated?: () => void;
}

/**
 * AR-445 — the Level-2 column of a Phase-1 module (the Settings column's shape).
 *
 * Its rows are the module's pages, any group headings ("About Our Tools"), and
 * Launch actions, which wear a button's border so a click that leaves for
 * another site never reads like a page of this one. The heading names the
 * module and opens its landing page.
 */
export const Phase1Column: React.FC<Phase1ColumnProps> = ({ module, inDrawer = false, onNavigated }) => {
  const [location] = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  if (!module.column?.length) return null;

  const heading = PHASE1_COLUMN_HEADINGS[module.id] ?? module.label;

  if (collapsed && !inDrawer) {
    return (
      <div
        data-app-chrome
        className="flex w-8 flex-col items-center border-r border-gray-200 bg-gray-50/50 pt-4 dark:border-gray-700 dark:bg-gray-900/50"
      >
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="rounded p-1 text-gray-400 transition-colors hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-color)] dark:text-gray-500 dark:hover:text-gray-300"
          aria-label={`Expand ${heading}`}
          title={`Expand ${heading}`}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    );
  }

  const rowCls = (active: boolean) =>
    cn(
      'flex w-full items-center rounded-lg px-3 text-left text-[14.5px] transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-color)]',
      inDrawer ? 'min-h-11 py-2.5' : 'py-2',
      active
        ? 'bg-[var(--sidebar-active)] font-semibold text-[var(--sidebar-active-text)]'
        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100',
    );

  return (
    <div
      data-app-chrome
      data-phase1-column={module.id}
      className={cn(
        'flex flex-col bg-gray-50/50 dark:bg-gray-900/50',
        inDrawer ? 'w-full' : 'min-h-0 w-60 border-r border-gray-200 dark:border-gray-700',
      )}
    >
      <div className={cn('flex items-center gap-1 px-3 pb-2', inDrawer ? 'pt-3' : 'pt-4')}>
        {!inDrawer && (
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            className="rounded p-1 text-gray-400 transition-colors hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-color)] dark:text-gray-500 dark:hover:text-gray-300"
            aria-label={`Collapse ${heading}`}
            title="Collapse sidebar"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}
        <Link
          href={module.path ?? '/'}
          onClick={onNavigated}
          aria-current={location === module.path ? 'page' : undefined}
          className="rounded px-1 text-[15px] font-bold text-gray-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-color)] dark:text-gray-100"
        >
          {heading}
        </Link>
      </div>

      <nav
        aria-label={heading}
        className={cn('px-3 pb-4', !inDrawer && 'flex-1 overflow-y-auto')}
      >
        <ul className="space-y-0.5">
          {module.column.map((item, i) => {
            if (item.kind === 'heading') {
              return (
                <li
                  key={`h-${i}`}
                  className="px-3 pb-1 pt-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400"
                >
                  {item.label}
                </li>
              );
            }
            const path = phase1Path(item.page);
            const active = location === path;
            if (item.kind === 'action') {
              return (
                <li key={item.page} className="pt-1">
                  <Link
                    href={path}
                    onClick={onNavigated}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'flex w-full items-center gap-1 rounded-lg border px-3 text-left text-[14.5px] transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-color)]',
                      inDrawer ? 'min-h-11 py-2.5' : 'py-2',
                      active
                        ? 'border-[var(--brand-color)] bg-[var(--sidebar-active)] font-semibold text-[var(--sidebar-active-text)]'
                        : 'border-gray-200 bg-white text-gray-800 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800',
                    )}
                  >
                    {item.label}
                    {item.external ? <ArrowUpRight className="h-3.5 w-3.5" aria-hidden /> : null}
                  </Link>
                </li>
              );
            }
            return (
              <li key={item.page}>
                <Link
                  href={path}
                  onClick={onNavigated}
                  aria-current={active ? 'page' : undefined}
                  className={rowCls(active)}
                >
                  {item.label ?? PHASE1_PAGES[item.page].title}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

export default Phase1Column;
